import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { ChevronRight, CircleArrowUp } from 'lucide-react';
import { Modal, Button } from '@getro/rombo';
import { useUpsellTracking } from 'hooks/useUpsellTracking';

/* Content enter: 0.9s block fade-up; shimmer waits 500ms after that */
const TOP_ACCENT_SHIMMER_DELAY = '1.4s';

/* Skewed streak reads as reflected light on a thin bar (not a flat gradient slide) */
const shimmerBloom = keyframes`
  0% {
    transform: translate3d(-140%, 0, 0) skewX(-14deg);
    opacity: 0.35;
  }
  100% {
    transform: translate3d(260%, 0, 0) skewX(-14deg);
    opacity: 0.45;
  }
`;

const shimmerGlint = keyframes`
  0% {
    transform: translate3d(-130%, 0, 0) skewX(-22deg);
    opacity: 0.5;
  }
  12% {
    opacity: 1;
  }
  88% {
    opacity: 1;
  }
  100% {
    transform: translate3d(300%, 0, 0) skewX(-22deg);
    opacity: 0.45;
  }
`;

const contentFadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const respectReducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
`;

const ModalContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  /* Cancel Rombo modal body horizontal padding (theme modals.default.body px: 24px) */
  margin: 0 -24px;
  width: calc(100% + 48px);
  box-sizing: border-box;
`;

const TopAccent = styled.div`
  position: absolute;
  left: 0;
  top: -25px;
  width: 100%;
  /* Slightly taller so the glint registers on screen; still reads as a hairline */
  height: 5px;
  flex-shrink: 0;
  transform-origin: left center;
  overflow: hidden;
  isolation: isolate;
  border-radius: 5px 5px 0 0;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors?.purple?.[400] ?? '#9b6bff'} 0%,
    ${({ theme }) => theme.colors?.purple?.[500]} 55%,
    ${({ theme }) => theme.colors?.purple?.[600] ?? '#5a32c9'} 100%
  );
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(0, 0, 0, 0.12);

  /* Soft wide bloom — chases the bar, adds depth */
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    bottom: -2px;
    left: 0;
    width: 70%;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 160, 210, 0.15) 42%,
      rgba(255, 200, 230, 0.35) 50%,
      rgba(255, 160, 210, 0.12) 58%,
      transparent 100%
    );
    filter: blur(1px);
    mix-blend-mode: screen;
    animation: ${shimmerBloom} 3.5s cubic-bezier(0.45, 0.05, 0.25, 1) ${TOP_ACCENT_SHIMMER_DELAY} infinite backwards;
  }

  /* Tight specular — reads as a highlight sweep */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 38%;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 210, 235, 0.2) 38%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 140, 200, 0.45) 62%,
      transparent 100%
    );
    mix-blend-mode: screen;
    animation: ${shimmerGlint} 3.5s cubic-bezier(0.45, 0.02, 0.2, 1) ${TOP_ACCENT_SHIMMER_DELAY} infinite backwards;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      animation: none !important;
      opacity: 0 !important;
    }
  }
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 48px 16px;
  animation: ${contentFadeUp} 0.9s cubic-bezier(0.22, 1, 0.36, 1) both;
  ${respectReducedMotion}
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/* Matches main menu upgrade affordance (menuNav.jsx showUpgradeIcon) */
const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EyebrowIconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  line-height: 0;
  color: ${({ theme }) => theme.colors?.purple?.[500]};
`;

const Eyebrow = styled.p`
  margin: 0;
  font-family: 'Silka', sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors?.purple?.[500]};
`;

const Title = styled.h2`
  margin: 0;
  font-family: 'Silka', sans-serif;
  font-weight: 700;
  font-size: 26px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const DescriptionRegion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionText = styled.p`
  margin: 0;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.main};
`;

const BridgeText = styled.p`
  margin: 0;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.main};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;

const SecondaryLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: fit-content;
  min-height: 0;
  width: fit-content;
  padding: 0 8px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.subtle};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors?.purple?.[500]};
    outline-offset: 2px;
  }
`;

const CTA_URL = 'https://scheduler.zoom.us/brett-bazzini/30-min-meeting';
const PRICING_URL = 'https://www.getro.com/pricing';

/** Shown under the feature-specific description, above the primary CTA (all variants). */
const BRIDGE_COPY = 'Our team can help you find the right plan and get you set up.';

const FALLBACK_UPGRADE_COPY = {
  title: 'Unlock this feature with a different plan',
  description: 'Get the most out of Getro by upgrading your plan.',
};

const UPGRADE_MODAL_COPY_BY_FEATURE = {
  contacts_import: {
    title: 'Sync your existing network',
    description:
      'Unlock more intro opportunities by growing your network with CSV uploads and automatic contacts sync.',
  },
  sharing_contacts_lists: {
    title: 'Share lists via public links',
    description: 'Share lists with customizable views and allow others to request intros.',
  },
  ask_for_intros: {
    title: 'Ask for intros to these companies',
    description:
      'Find intro opportunities by sharing this list with anyone. They can connect their LinkedIn and offer intros.',
  },
  invite_allies: {
    title: 'Invite allies to share their contacts',
    description: "Scale intros by integrating your allies' network connections.",
  },
};

function getUpgradeModalCopy(featureName) {
  return UPGRADE_MODAL_COPY_BY_FEATURE[featureName] ?? FALLBACK_UPGRADE_COPY;
}

const UpgradeModal = ({ isOpen, onClose, featureName }) => {
  const { trackPaywallShown, trackDemoClick } = useUpsellTracking();
  const { title, description } = getUpgradeModalCopy(featureName);

  useEffect(() => {
    if (isOpen) {
      trackPaywallShown(featureName);
    }
  }, [isOpen, featureName, trackPaywallShown]);

  const handleCtaClick = () => {
    trackDemoClick(featureName);
    window.open(CTA_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalContent data-testid="upgrade-modal">
        <TopAccent aria-hidden />

        <Inner>
          <Copy>
            <EyebrowRow>
              <EyebrowIconWrap>
                <CircleArrowUp size={16} aria-hidden strokeWidth={1.5} />
              </EyebrowIconWrap>
              <Eyebrow>Feature available on other plans</Eyebrow>
            </EyebrowRow>
            <Title id="upgrade-modal-title">{title}</Title>
            <DescriptionRegion id="upgrade-modal-description">
              <DescriptionText>{description}</DescriptionText>
              <BridgeText>{BRIDGE_COPY}</BridgeText>
            </DescriptionRegion>
          </Copy>
          <Actions>
            <Button
              onClick={handleCtaClick}
              variant="primary"
              aria-describedby="upgrade-modal-description"
              sx={{
                width: '100%',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              Schedule a call
              <ChevronRight size={18} aria-hidden strokeWidth={2.25} />
            </Button>
            <SecondaryLink
              href={PRICING_URL}
              target="_blank"
              rel="noopener noreferrer"
              title="Opens getro.com pricing in a new tab"
            >
              View plans and features
            </SecondaryLink>
          </Actions>
        </Inner>
      </ModalContent>
    </Modal>
  );
};

UpgradeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  featureName: PropTypes.string.isRequired,
};

export default UpgradeModal;
